import { useState } from 'react';
import { css } from '@emotion/react';
import { V1 as AiSettingsV1 } from '@frontend/api-ai-settings';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  DatePickerField,
  DayPicker,
  DaysOfWeek,
  DropdownField,
  FormRow,
  FormValues,
  RadioField,
  Text,
  TimeField,
} from '@frontend/design-system';
import { abbreviateDays } from '../../utils/utilities';
import { SubduedTitle, Title } from '../availability-rules/titles';
import { FormWrapperChildrenProps } from '../form-wrapper';
import { MultiSelect, MultiSelectItem } from './multi-select';
import { EXCEPTION_RECURRENCE_OPTIONS, EXCEPTION_TARGET, RuleExceptionFieldTypes } from './rule-exceptions-form-config';
import { getTimeRangeState } from './rule-exceptions-utils';

interface TimeRangeErrorProps {
  isEndTimeInvalid: boolean;
  isStartRequiredError: boolean;
  isEndRequiredError: boolean;
}

const TimeRangeError = ({ isEndTimeInvalid, isStartRequiredError, isEndRequiredError }: TimeRangeErrorProps) => {
  const { t } = useTranslation('schedule-settings');

  let errorText = '';
  if (isEndTimeInvalid) {
    errorText = t('End time must be after start time');
  } else if (isStartRequiredError && isEndRequiredError) {
    errorText = t('Start time and end time are required');
  } else if (isStartRequiredError) {
    errorText = t('Start time is required');
  } else if (isEndRequiredError) {
    errorText = t('End time is required');
  }

  if (!errorText) return null;

  return (
    <Text size='small' color='error' css={{ marginTop: theme.spacing(-3), marginBottom: theme.spacing(2) }}>
      {errorText}
    </Text>
  );
};

interface RuleExceptionsFormProps {
  getFieldProps: FormWrapperChildrenProps<RuleExceptionFieldTypes>['getFieldProps'];
  values: FormValues<RuleExceptionFieldTypes>;
  providersData: AiSettingsV1.Types.GetSchedulingStaffsIO['output'] | undefined;
  operatoriesData: AiSettingsV1.Types.GetSchedulingResourcesIO['output'] | undefined;
  servicesData: AiSettingsV1.Types.GetSchedulingAppointmentTypesIO['output'] | undefined;
}

export const RuleExceptionsForm = ({
  getFieldProps,
  values,
  providersData,
  operatoriesData,
  servicesData,
}: RuleExceptionsFormProps) => {
  const { t } = useTranslation('schedule-settings');

  const [selectedDays, setSelectedDays] = useState<DaysOfWeek[]>((values.daysOfWeek as DaysOfWeek[]) ?? []);

  const isWeekly = values.recurrence === 'WEEKLY';
  const { isStartRequiredError, isEndRequiredError, isEndTimeInvalid } = getTimeRangeState(values, getFieldProps);
  const isAllDay = values.isAllDay;

  const isChosen = values.exceptionTarget === EXCEPTION_TARGET.CHOSEN;

  const providerItems: MultiSelectItem[] = (providersData?.staffs ?? []).map((provider) => ({
    id: provider.id ?? '',
    label: provider.name ?? '',
  }));
  const operatoryItems: MultiSelectItem[] = (operatoriesData?.resources ?? []).map((operatory) => ({
    id: operatory.id ?? '',
    label: operatory.name ?? '',
  }));
  const serviceItems: MultiSelectItem[] = (servicesData?.appointmentTypes ?? []).map((service) => ({
    id: service.id ?? '',
    label: service.name ?? '',
  }));

  const specificDateFieldProps = getFieldProps('specificDate');
  const daysOfWeekFieldProps = getFieldProps('daysOfWeek');
  return (
    <>
      <SubduedTitle>{t('Recurrence')}</SubduedTitle>
      <FormRow cols={[100]} css={{ marginBottom: theme.spacing(2) }}>
        <DropdownField {...getFieldProps('recurrence')} label=''>
          {EXCEPTION_RECURRENCE_OPTIONS.map((option) => (
            <DropdownField.Option key={option.value} value={option.value}>
              {t(option.label)}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </FormRow>

      {!daysOfWeekFieldProps.hidden && (
        <>
          <Text
            color='subdued'
            size='small'
            css={{ fontWeight: theme.font.weight.bold, marginBottom: theme.spacing(1) }}
          >
            {t('Day(s) of the week')}
          </Text>
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(1),
              marginBottom: theme.spacing(2),
            }}
          >
            <DayPicker
              {...daysOfWeekFieldProps}
              selectedDays={selectedDays}
              config={{
                onDayToggle: ({ toggledDays, selectedDays: days }) => {
                  setSelectedDays(days);
                  toggledDays.forEach((day) => daysOfWeekFieldProps.onChange({ name: 'daysOfWeek', value: day }));
                },
              }}
            />
            {!selectedDays.length && daysOfWeekFieldProps.touched && (
              <Text size='small' color='error'>
                {t('Select a day of the week')}
              </Text>
            )}
            {selectedDays.length > 0 && (
              <Text size='small'>
                {t('Selected: {{selectedDaysOfWeek}}', {
                  selectedDaysOfWeek: abbreviateDays(selectedDays).join(', '),
                })}
              </Text>
            )}
          </div>
        </>
      )}
      {!specificDateFieldProps.hidden && (
        <>
          <SubduedTitle>{t('Date')}</SubduedTitle>
          <FormRow cols={[100]} css={{ marginBottom: theme.spacing(2) }}>
            <DatePickerField {...specificDateFieldProps} label='' css={{ maxWidth: '100%' }} required={!isWeekly} />
          </FormRow>
        </>
      )}

      <section
        css={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: theme.spacing(1),
          width: '100%',
          margin: `${theme.spacing(4)} 0`,
        }}
      >
        <CheckboxField {...getFieldProps('isAllDay')} label={t('All day')} labelPlacement='right' css={{ width: 90 }} />
        <div css={{ width: 1, height: 40, backgroundColor: theme.colors.neutral20, marginRight: theme.spacing(1) }} />
        <TimeField
          {...getFieldProps('startTime')}
          label={t('Start Time')}
          css={{ maxWidth: 150 }}
          hideErrorText
          disabled={isAllDay}
        />
        <span>{t('to')}</span>
        <TimeField
          {...getFieldProps('endTime')}
          minTime={values.startTime}
          label={t('End Time')}
          css={{ maxWidth: 150 }}
          hideErrorText
          disabled={isAllDay}
        />
      </section>
      <TimeRangeError
        isEndTimeInvalid={isEndTimeInvalid}
        isStartRequiredError={isStartRequiredError}
        isEndRequiredError={isEndRequiredError}
      />

      <Title css={{ fontSize: theme.fontSize(16) }}>{t('Choose the Exception Target')}</Title>
      <RadioField
        {...getFieldProps('exceptionTarget')}
        label=''
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1),
          margin: `${theme.spacing(2)} 0`,
        }}
      >
        <section css={exceptionTargetContainerStyle}>
          <RadioField.Radio value={EXCEPTION_TARGET.ALL} css={exceptionTargetRadioStyle}>
            {t('Apply to all')}
          </RadioField.Radio>
          <Text size='small' color='subdued' css={{ marginLeft: theme.spacing(3.5) }}>
            {t('All resources at this location')}
          </Text>
        </section>
        <section css={exceptionTargetContainerStyle}>
          <RadioField.Radio value={EXCEPTION_TARGET.CHOSEN} css={exceptionTargetRadioStyle}>
            {t('Apply to chosen')}
          </RadioField.Radio>
          <Text size='small' color='subdued' css={{ marginLeft: theme.spacing(3.5) }}>
            {t('Specific resources at this location')}
          </Text>
        </section>
      </RadioField>

      {isChosen && (
        <section css={exceptionTargetOptionsContainerStyle}>
          <MultiSelect
            title={t('Providers')}
            iconName='user-small'
            items={providerItems}
            fieldProps={getFieldProps('selectedProviderIds')}
          />
          <MultiSelect
            title={t('Operatories')}
            iconName='department-small'
            items={operatoryItems}
            fieldProps={getFieldProps('selectedResourceIds')}
          />
          <MultiSelect
            title={t('Services')}
            iconName='tile-small'
            items={serviceItems}
            fieldProps={getFieldProps('selectedServiceIds')}
          />
        </section>
      )}
    </>
  );
};

const exceptionTargetContainerStyle = css({
  padding: theme.spacing(2),
  border: `1px solid ${theme.colors.neutral40}`,
  borderRadius: theme.borderRadius.medium,
});

const exceptionTargetRadioStyle = css({
  marginBottom: theme.spacing(0.5),
  fontWeight: theme.font.weight.bold,
});

const exceptionTargetOptionsContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.colors.primary10,
  padding: theme.spacing(2),
  borderRadius: theme.borderRadius.medium,
  marginBottom: theme.spacing(2),
});
